import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const comparison = [
  { label: 'Aulas simultâneas', traditional: 'Apenas 1 por sala (conflito de áudio)', silent: 'Até 10 no mesmo espaço' },
  { label: 'Qualidade do áudio', traditional: 'Reverberação, eco, zonas mortas', silent: 'Voz cristalina direto no ouvido' },
  { label: 'Distração dos alunos', traditional: 'Conversas paralelas, ruído externo', silent: 'Foco total com isolamento acústico' },
  { label: 'Aproveitamento do espaço', traditional: '1 sala = 1 aula', silent: '1 sala = até 10 aulas simultâneas' },
  { label: 'Conforto do instrutor', traditional: 'Precisa elevar a voz, cansaço vocal', silent: 'Fala em tom natural, microfone capta' },
  { label: 'Inclusão auditiva', traditional: 'Volume único para todos', silent: 'Cada aluno ajusta seu volume ideal' },
  { label: 'Aulas multilíngues', traditional: 'Impossível sem salas separadas', silent: 'Cada canal = 1 idioma' },
  { label: 'Setup e logística', traditional: 'Isolamento acústico entre salas', silent: 'Apenas transmissores e fones — 30 min' },
];

const benefits = [
  {
    icon: 'ri-building-4-line',
    title: 'Otimização de Espaço',
    description: 'Transforme 1 sala em 3 aulas simultâneas. Reduza custos de aluguel, energia e infraestrutura. Ideal para escolas, academias e centros de treinamento que precisam escalar.',
    number: '3x',
    numberLabel: 'mais aulas por m²',
  },
  {
    icon: 'ri-brain-line',
    title: 'Maior Retenção de Conteúdo',
    description: 'O isolamento acústico elimina distrações e aumenta o foco. Alunos relatam compreensão até 40% superior em comparação com aulas tradicionais em ambientes compartilhados.',
    number: '+40%',
    numberLabel: 'retenção de conhecimento',
  },
  {
    icon: 'ri-global-line',
    title: 'Acessibilidade e Inclusão',
    description: 'Volume individual para cada aluno. Quem tem deficiência auditiva ajusta conforme necessidade. Aulas em múltiplos idiomas no mesmo espaço — inclusão real.',
    number: '100%',
    numberLabel: 'acessível para todos',
  },
];

export default function BenefitsSection() {
  const { ref: compRef, isVisible: compVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: benRef, isVisible: benVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      {/* Comparison table */}
      <section ref={compRef} className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Comparativo
            </span>
            <h2
              className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              aula silenciosa vs. aula tradicional
            </h2>
          </div>

          <div
            className={`bg-white rounded-3xl overflow-hidden border border-gray-100 transition-all duration-700 delay-200 ${compVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Table header */}
            <div className="grid grid-cols-3 bg-secondary text-white">
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider text-white/60">Critério</div>
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider border-l border-white/10">Aula Tradicional</div>
              <div className="p-5 font-body text-sm font-semibold uppercase tracking-wider border-l border-white/10 text-primary">Aula Silenciosa</div>
            </div>

            {/* Rows */}
            {comparison.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} transition-all duration-500 ${compVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ transitionDelay: `${200 + i * 60}ms` }}
              >
                <div className="p-5 font-body text-sm font-semibold text-secondary">{row.label}</div>
                <div className="p-5 border-l border-gray-100">
                  <span className="inline-flex items-center gap-2 font-body text-sm text-gray-500">
                    <i className="ri-close-circle-line text-red-400 text-base flex-shrink-0" />
                    {row.traditional}
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100">
                  <span className="inline-flex items-center gap-2 font-body text-sm text-gray-700 font-medium">
                    <i className="ri-checkbox-circle-line text-primary text-base flex-shrink-0" />
                    {row.silent}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits highlight */}
      <section ref={benRef} className="py-28 bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2
              className={`font-heading font-bold text-5xl text-white mb-4 transition-all duration-700 ${benVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              por que sua instituição precisa disso
            </h2>
            <p className={`font-body text-lg text-white/60 transition-all duration-700 delay-150 ${benVisible ? 'opacity-100' : 'opacity-0'}`}>
              Resultados mensuráveis que transformam a experiência de ensino
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:bg-white/10 transition-all duration-500 ${benVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary/20 mx-auto mb-6">
                  <i className={`${b.icon} text-primary text-3xl`} />
                </div>
                <div className="font-heading font-extrabold text-5xl text-primary mb-1">{b.number}</div>
                <div className="font-body text-xs text-white/50 uppercase tracking-wider mb-5">{b.numberLabel}</div>
                <h3 className="font-heading font-bold text-xl text-white mb-3">{b.title}</h3>
                <p className="font-body text-sm text-white/65 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}